import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import {
  applyNodeChanges,
  Controls,
  type Edge,
  MiniMap,
  type Node,
  type OnNodesChange,
  ReactFlow,
  type ReactFlowInstance,
  type ReactFlowProps,
} from '@xyflow/react'
import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { type IFlowAction } from 'widgets/Flow/types'
import { ZoomControls } from 'widgets/Flow/ui/ZoomControls/ZoomControls'
import { getInitialNode } from 'widgets/Flow/utils/getInitialNode'

import './react-flow.scss'
import styles from './styles.module.scss'

export type IFlowProps = {
  actions: IFlowAction[]
  nodeTypes: ReactFlowProps['nodeTypes']
  edgeTypes: ReactFlowProps['edgeTypes']
  getNodesByChangesMap: (props: {
    nodeChangesMap: Map<string, Node>
    containerWidth: number
  }) => Node[]
  selectedAction: ReactElement
  viewOnly?: boolean
  fitViewOnChange?: boolean
}

export const Flow = observer(
  ({
    actions,
    fitViewOnChange,
    nodeTypes,
    edgeTypes,
    getNodesByChangesMap,
    selectedAction,
    viewOnly,
  }: IFlowProps) => {
    const ref = useRef<HTMLDivElement>(null)
    const [nodes, setNodes] = useState<Node[]>([])
    const [edges, setEdges] = useState<Edge[]>([])
    const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null)
    const fitViewPendingRef = useRef(false)
    const fitViewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
      const containerWidth = ref.current?.clientWidth || 0
      const oldDodeChangesMap = new Map(nodes.map((node) => [node.id, node]))
      const nodeChangesMap = new Map()

      actions.map((action) => {
        const current = oldDodeChangesMap.get(action.actionStore.id)
        if (current) {
          nodeChangesMap.set(current.id, current)
          return
        }
        const newNode = getInitialNode(action)
        nodeChangesMap.set(newNode.id, newNode)
      })
      setNodes(
        getNodesByChangesMap({
          nodeChangesMap,
          containerWidth,
        })
      )
      const newEdges: Edge[] = []
      actions.forEach((action) => {
        action.actionStore.nextIds.forEach((nextId: string) => {
          newEdges.push({
            id: `${action.actionStore.id}-${nextId}`,
            source: action.actionStore.id,
            target: nextId,
            type: action.actionStore.edgeType,
          })
        })
      })
      setEdges(newEdges)
      if (fitViewOnChange) {
        fitViewPendingRef.current = true
        const fallbackId = setTimeout(() => {
          if (!fitViewPendingRef.current) return
          fitViewPendingRef.current = false
          reactFlowInstanceRef.current?.fitView({ minZoom: 0.05, padding: '40px' })
        }, 500)
        return () => {
          clearTimeout(fallbackId)
          if (fitViewTimerRef.current) {
            clearTimeout(fitViewTimerRef.current)
            fitViewTimerRef.current = null
          }
        }
      }
    }, [actions])

    const onNodesChange: OnNodesChange = useCallback(
      (changes) => {
        const containerWidth = ref.current?.clientWidth || 0
        setNodes((nds) => {
          const nodeChangesMap = new Map(
            applyNodeChanges(changes, nds).map((node) => [node.id, node])
          )

          return getNodesByChangesMap({
            nodeChangesMap,
            containerWidth,
          })
        })
        if (fitViewPendingRef.current && changes.some((c) => c.type === 'dimensions')) {
          if (fitViewTimerRef.current) clearTimeout(fitViewTimerRef.current)
          fitViewTimerRef.current = setTimeout(() => {
            fitViewPendingRef.current = false
            fitViewTimerRef.current = null
            reactFlowInstanceRef.current?.fitView({ minZoom: 0.05, padding: '40px' })
          }, 100)
        }
      },
      [setNodes]
    )

    const onInit = useCallback((instance: ReactFlowInstance) => {
      reactFlowInstanceRef.current = instance
    }, [])

    const viewOnlyProps: ReactFlowProps | undefined = viewOnly
      ? {
          fitView: true,
          nodesDraggable: false,
          nodesConnectable: false,
          nodesFocusable: false,
          edgesFocusable: false,
          panOnDrag: false,
          elementsSelectable: false,
          zoomOnScroll: false,
          zoomOnPinch: false,
          zoomOnDoubleClick: false,
          disableKeyboardA11y: true,
        }
      : undefined

    return (
      <div className={classNames(styles.wrap, { [styles.viewOnly]: viewOnly })} ref={ref}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          draggable={false}
          maxZoom={1}
          minZoom={fitViewOnChange ? 0.05 : 0.2}
          translateExtent={[
            [-15000, -15000],
            [15000, 15000],
          ]}
          onInit={fitViewOnChange ? onInit : undefined}
          deleteKeyCode={null}
          {...viewOnlyProps}
        >
          {selectedAction}
          {!viewOnly && (
            <>
              <Controls
                showFitView={false}
                showInteractive={false}
                showZoom={false}
                orientation={'horizontal'}
              >
                <ZoomControls />
              </Controls>
              <MiniMap zoomable pannable zoomStep={5} />
            </>
          )}
        </ReactFlow>
      </div>
    )
  }
)
